import React from 'react';
import { cn } from '@/lib/utils';

interface HighlightedSnippetProps {
  /**
   * Snippet text from the search_loan_attachment_content RPC. Matched terms
   * are wrapped in [[MARK]] / [[/MARK]] sentinels. Everything between (and
   * outside) is plain text — no HTML. We never trust this string as markup;
   * it's rendered as React text nodes plus <mark> elements.
   */
  snippet: string | null | undefined;
  className?: string;
}

const SENTINEL_RE = /\[\[\/?MARK\]\]/;

/**
 * Renders a search-result snippet by splitting on [[MARK]] / [[/MARK]]
 * sentinels and producing alternating text/<mark> React nodes.
 *
 * Compared to dangerouslySetInnerHTML: equivalent UX, zero XSS surface, no
 * manual HTML escaping needed because the OCR-extracted text never becomes
 * markup.
 */
export function HighlightedSnippet({ snippet, className }: HighlightedSnippetProps) {
  if (!snippet) {
    return null;
  }

  // Split on the sentinel pair and toggle highlighted vs plain on each segment.
  // The regex matches both opening and closing markers, so every other piece
  // (after the first plain piece) is highlighted.
  const parts = snippet.split(SENTINEL_RE);

  return (
    <span className={cn('text-xs text-muted-foreground', className)}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-yellow-100 text-foreground rounded px-0.5">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </span>
  );
}
